import { Link } from "@tanstack/react-router";
import { CheckCircle, GearSix, Lightning, LinkSimple, Target, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SettingsStatusTone, computeTherapistSettingsStatus } from "@/lib/therapist-settings-status";
import { StatusPill, toneClasses } from "./section-primitives";
import type { ProfileWithMeta, SettingsSection } from "./types";

export function OverviewSection({
  status,
  onSection,
}: {
  status: NonNullable<ReturnType<typeof computeTherapistSettingsStatus>>;
  profile: ProfileWithMeta;
  activeServices: number;
  availabilityCount: number;
  onSection: (section: SettingsSection) => void;
}) {
  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-border/60 bg-card px-5 py-6 shadow-sm sm:px-8">
        <StatusPill tone={status.reservations.tone} label={status.reservations.label} />
        <h2
          className="mt-3 text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {status.reservations.ready ? "Profil je spreman za rezervacije" : "Profil još nije spreman za rezervacije"}
        </h2>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">{status.reservations.detail}</p>
        {status.reservations.blockers.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {status.reservations.blockers.map((b) => (
              <span
                key={b}
                className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200"
              >
                {b}
              </span>
            ))}
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-3">
        <QuickStatusCard
          title="Rezervacije"
          label={status.reservations.label}
          detail={
            status.reservations.blockers.length > 0
              ? status.reservations.blockers.join(" · ")
              : "Dostupnost i usluge su spremne."
          }
          tone={status.reservations.tone}
          actions={[
            { label: "Uredi dostupnost", onClick: () => onSection("availability") },
            { label: "Idi na usluge", href: "/dashboard/therapist/services" },
          ]}
        />
        <QuickStatusCard
          title="Javni profil"
          label={status.publicProfile.label}
          detail={status.publicProfile.detail}
          tone={status.publicProfile.tone}
          actions={[{ label: "Uredi profil", onClick: () => onSection("profile") }]}
        />
        <QuickStatusCard
          title="Administracija"
          label={status.administration.label}
          detail={status.administration.detail}
          tone={status.administration.tone}
          actions={[{ label: "Uredi naplatu", onClick: () => onSection("billing") }]}
        />
      </div>

      <div className="divide-y divide-border/60 overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
        <OptionalRow
          icon={Target}
          label="Matching"
          value={status.optional.matchingReady ? "Popunjen" : "Nije popunjen"}
          active={status.optional.matchingReady}
          onClick={() => onSection("matching")}
        />
        <OptionalRow
          icon={Lightning}
          label="Hitne konsultacije"
          value={status.optional.emergencyEnabled ? "Uključene" : "Isključene"}
          active={status.optional.emergencyEnabled}
          onClick={() => onSection("emergency")}
        />
        {/* Privremeno skriveno: Google Calendar red
        <OptionalRow
          icon={LinkSimple}
          label="Google Calendar"
          value={status.optional.googleConnected ? "Povezan" : "Nije povezan"}
          active={status.optional.googleConnected}
          onClick={() => onSection("integrations")}
        />
        */}
      </div>
    </div>
  );
}

function QuickStatusCard({
  title,
  label,
  detail,
  tone,
  actions,
}: {
  title: string;
  label: string;
  detail: string;
  tone: SettingsStatusTone;
  actions: Array<{ label: string; onClick?: () => void; href?: string }>;
}) {
  return (
    <article className="flex flex-col rounded-2xl border border-border/60 bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
          {title}
        </h3>
        <span className={cn("size-2 shrink-0 rounded-full", toneClasses(tone).dot)} />
      </div>
      <p className="mt-1 text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{detail}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {actions.map((action) =>
          action.href ? (
            <Button key={action.label} asChild size="sm" variant="outline">
              <Link to={action.href}>{action.label}</Link>
            </Button>
          ) : (
            <Button key={action.label} size="sm" variant="outline" onClick={action.onClick}>
              {action.label}
            </Button>
          ),
        )}
      </div>
    </article>
  );
}

function OptionalRow({
  icon: Icon,
  label,
  value,
  active,
  onClick,
}: {
  icon: typeof GearSix;
  label: string;
  value: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-muted/40"
    >
      <Icon className="size-5 shrink-0 text-muted-foreground" weight="duotone" />
      <span className="min-w-0 flex-1">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span className="ml-2 text-xs text-muted-foreground">{value}</span>
      </span>
      {active ? (
        <CheckCircle className="size-5 shrink-0 text-emerald-600" weight="fill" />
      ) : (
        <WarningCircle className="size-5 shrink-0 text-amber-600" />
      )}
    </button>
  );
}
